import React from "react";
import { Link } from "react-router-dom";
import { Book, Code, FileCheck, Rocket, Shield, Terminal } from "lucide-react";

import { PublicLayout } from "@/components/layout/PublicLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from "@/lib/i18n";

export default function HandoverPage() {
  const { t } = useTranslation();

  return (
    <PublicLayout>
      <div className="space-y-10">
        <div className="flex items-center gap-4">
          <div className="rounded-2xl bg-primary/10 p-3 text-primary">
            <Book className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight">{t("handover_title")}</h1>
            <p className="mt-1 text-muted-foreground">{t("handover_desc")}</p>
          </div>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="h-auto w-full overflow-x-auto bg-muted/50 p-1 md:w-auto">
            <TabsTrigger value="overview">Tổng quan</TabsTrigger>
            <TabsTrigger value="deployment">Triển khai</TabsTrigger>
            <TabsTrigger value="api">API Mocking</TabsTrigger>
            <TabsTrigger value="architecture">Kiến trúc</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border-white/30 bg-card/70 backdrop-blur-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Rocket className="h-5 w-5 text-primary" /> Tính năng chính
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p>• Điều phối workspace đa tenant</p>
                  <p>
                    • Bộ máy cấu hình agent chuyên biệt (
                    <Link to="/docs/agents" className="font-bold text-primary hover:underline">
                      xem đặc tả kỹ thuật
                    </Link>
                    )
                  </p>
                  <p>• Governance cho doanh nghiệp và hỗ trợ chấm điểm tuân thủ AI</p>
                  <p>• Audit log bất biến</p>
                  <p>• Hỗ trợ song ngữ và lưu trạng thái cục bộ</p>
                </CardContent>
              </Card>

              <Card className="border-white/30 bg-card/70 backdrop-blur-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileCheck className="h-5 w-5 text-primary" /> Hướng dẫn nhanh
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p>1. Đăng nhập qua cổng quản trị.</p>
                  <p>2. Tạo agent trong màn hình Agents.</p>
                  <p>3. Khai báo tools và API cần dùng.</p>
                  <p>4. Theo dõi sức khỏe hệ thống ở Dashboard.</p>
                  <p>5. Kiểm tra audit log cho các yêu cầu tuân thủ.</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="deployment">
            <Card className="border-white/30 bg-card/70 backdrop-blur-xl">
              <CardHeader>
                <CardTitle>Hạ tầng Cloudflare</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex gap-4">
                  <Terminal className="mt-1 h-5 w-5 shrink-0 text-primary" />
                  <div className="space-y-2">
                    <h4 className="font-bold">Cấu hình Wrangler</h4>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Dự án đã được cấu hình với Durable Object dùng cho các thao tác lưu trữ nguyên tử. Triển khai bằng <code>bun run deploy</code>. Hãy bảo đảm các biến môi trường quan trọng đã được khai báo trước khi đẩy bản build lên môi trường thật.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Code className="mt-1 h-5 w-5 shrink-0 text-primary" />
                  <div className="space-y-2">
                    <h4 className="font-bold">Frontend Hydration</h4>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Frontend React + Vite được tối ưu qua Tailwind và Zustand. Trạng thái phiên làm việc được lưu cục bộ để trải nghiệm chuyển màn hình liền mạch hơn.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="api">
            <Card className="border-white/30 bg-card/70 backdrop-blur-xl">
              <CardHeader>
                <CardTitle>Shared Types & API Schema</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="overflow-x-auto rounded-lg bg-slate-950 p-4 text-xs text-green-400">
                  {`interface ApiResponse<T> {\n  success: boolean;\n  data?: T;\n  error?: string;\n}\n\n// Backend Entities:\nclass UserEntity extends IndexedEntity<User>\nclass ChatBoardEntity extends IndexedEntity<ChatBoardState>`}
                </pre>
                <p className="mt-4 text-sm italic text-muted-foreground">Các route API chính đang được tổ chức trong worker và dùng chung helper để chuẩn hóa phản hồi.</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="architecture">
            <div className="rounded-3xl border-2 border-dashed p-12 text-center">
              <Shield className="mx-auto h-12 w-12 text-muted-foreground/30" />
              <h3 className="mt-4 font-semibold">Mô hình kiến trúc doanh nghiệp</h3>
              <p className="mx-auto max-w-sm text-sm text-muted-foreground">Sơ đồ tổng quan và mô hình dữ liệu chi tiết có thể được bàn giao kèm tài liệu nội bộ.</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PublicLayout>
  );
}
